import { ErrorMessage } from '@hookform/error-message';

import { ReactNode, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { Button, FormControl, FormItem, Modal, OptionList, Select, SelectTrigger } from '@ingrid/luterkort-react';
import { ShippingLogo } from '@ingrid/react-shipping-logo';

import { useTranslation } from '~src/i18n/use-translation';
import { useShippingMethodListSuspenseQuery } from '~src/main/queries';
import { ShipmentUpsert } from '~src/pages/shipment/types';

type FormValues = Pick<ShipmentUpsert, 'shipping_method'>;

export interface CarrierProductSelectProps {
  mode: 'add' | 'update';
  value?: FormValues;
  onSubmit: (values: FormValues) => void;
  trigger: ReactNode;
}

export function CarrierProductSelect({ mode, value, onSubmit: onSubmitProp, trigger }: CarrierProductSelectProps) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: value,
  });
  const { data: shippingMethods } = useShippingMethodListSuspenseQuery({ customBookingMethods: true });

  const onSubmit: SubmitHandler<FormValues> = values => {
    onSubmitProp(values);
    setModalOpen(false);
  };

  return (
    <FormControl.Root>
      <Modal.Root
        open={modalOpen}
        onOpenChange={o => {
          setModalOpen(o);
          if (!o) {
            reset(value);
          }
        }}
      >
        <Modal.Trigger>{trigger}</Modal.Trigger>

        <Modal.Content>
          <FormControl.Form onSubmit={handleSubmit(onSubmit)} style={{ display: 'none' }} />

          <Modal.Header title={t('common.carrierProduct')} />

          <Modal.Body css={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <Controller
              control={control}
              name="shipping_method"
              rules={{
                required: t('errors.fieldIsRequired'),
              }}
              render={({ field: { onChange, value } }) => (
                <FormItem.Root>
                  <FormItem.Label>{t('common.carrierProduct')}</FormItem.Label>
                  <FormItem.Control>
                    <Select.Root
                      value={value?.id ? [value.id] : undefined}
                      onValueChange={v => onChange(shippingMethods.find(c => c.id === v[0]))}
                    >
                      <Select.Trigger data-invalid={!!errors.shipping_method}>
                        <SelectTrigger.Button>
                          <SelectTrigger.Placeholder>{t('common.selectCarrierProduct')}</SelectTrigger.Placeholder>

                          {shippingMethods.map(method => (
                            <SelectTrigger.Value key={method.id} value={method.id!}>
                              <SelectTrigger.Logo>
                                <ShippingLogo method={method.id} width={30} />
                              </SelectTrigger.Logo>
                              <span>{method.name}</span>
                            </SelectTrigger.Value>
                          ))}
                          <SelectTrigger.Icon />
                        </SelectTrigger.Button>
                      </Select.Trigger>

                      <Select.Content css={{ width: '608px' }}>
                        <OptionList.Viewport>
                          {shippingMethods.map(carrier => (
                            <OptionList.Item key={carrier.id} value={carrier.id!}>
                              <OptionList.ItemLogo>
                                <ShippingLogo method={carrier.id} width={30} />
                              </OptionList.ItemLogo>
                              <OptionList.ItemText>{carrier.name}</OptionList.ItemText>
                              <OptionList.ItemIndicator />
                            </OptionList.Item>
                          ))}
                        </OptionList.Viewport>
                      </Select.Content>
                    </Select.Root>
                  </FormItem.Control>
                  <ErrorMessage as={FormItem.ErrorText} errors={errors} name="shipping_method" />
                </FormItem.Root>
              )}
            />
          </Modal.Body>
          <Modal.Footer>
            <Modal.CloseButton>
              <Button variant="secondary">{t('common.cancel')}</Button>
            </Modal.CloseButton>
            <FormControl.SubmitButton asChild>
              <Button>{t(mode === 'add' ? 'common.addCarrier' : 'common.updateCarrier')}</Button>
            </FormControl.SubmitButton>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </FormControl.Root>
  );
}
